import React, { useEffect, useRef, useState } from 'react';
import MasterLayout from '../masterLayout/MasterLayout';
import $ from 'jquery';
import 'datatables.net-dt/js/dataTables.dataTables.js';
import 'datatables.net-responsive-dt/js/responsive.dataTables.js';
import 'datatables.net-responsive-dt/css/responsive.dataTables.css';
import 'datatables.net-buttons/js/dataTables.buttons.min.js';
import 'datatables.net-buttons/js/buttons.html5.min.js';
import 'datatables.net-buttons/js/buttons.print.min.js';
import 'datatables.net-buttons-dt/css/buttons.dataTables.min.css';
import { Icon } from '@iconify/react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_URL } from '..';
import ExpenseSummaryCard from '../pages/ExpenseSummaryCard';
import { useNavigate } from 'react-router-dom';
import { Nav, Tab } from 'react-bootstrap';

const ExpenseTablePage = () => {
  const [expenses, setExpenses] = useState([]);
  const [trucksExpenses, setTrucksExpenses] = useState([]);
  const [activeTab, setActiveTab] = useState('office_operations');
  const officeTableRef = useRef(null);
  const staffTableRef = useRef(null);
  const trucksTableRef = useRef(null);
  const officeDataTableRef = useRef(null);
  const staffDataTableRef = useRef(null);
  const trucksDataTableRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) {
      window.location.href = '/sign-in';
    } else {
      fetchExpenses(user.id);
    }

    return () => {
      // Cleanup DataTables
      if (officeDataTableRef.current) officeDataTableRef.current.destroy();
      if (staffDataTableRef.current) staffDataTableRef.current.destroy();
      if (trucksDataTableRef.current) trucksDataTableRef.current.destroy();
    };
  }, []);

  const fetchExpenses = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/api/expense/${userId}`, {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
        },
      });
      if (response.status === 200) {
        // Normalize expenses data
        const normalizedExpenses = response.data.expenses.map((expense) => ({
          id: expense.id,
          title: expense.title || '-',
          description: expense.description || '-',
          amount: expense.amount || 0,
          payment_method: expense.payment_method || '-',
          createdAt: expense.createdAt || '-',
          updatedAt: expense.updatedAt || '-',
          account: expense.account || null,
          employee: expense.employee || null,
          on_date: expense.on_date || '-',
          truck: expense.truck || null,
          expense_type: expense.expense_type || 'unknown',
          DT_RowId: `row_${expense.id}`,
        }));
        // Normalize trucks data
        const normalizedTrucks = response.data.trucks.map((truck) => ({
          id: truck.id,
          title: truck.expense_title || '-',
          description: truck.expense_details || '-',
          amount: truck.amount || 0,
          payment_method: truck.payment_method || '-',
          createdAt: truck.createdAt || '-',
          updatedAt: truck.updatedAt || '-',
          on_date: truck.date || '-',
          truck: truck.truck || null,
          expense_type: 'truck',
          DT_RowId: `truck_row_${truck.id}`,
        }));
        setTrucksExpenses(normalizedTrucks);
        setExpenses(normalizedExpenses);
      } else {
        Swal.fire('Error', response.data.message || 'Failed to fetch expenses', 'error');
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch expenses: ' + error.message, 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You won’t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(`${API_URL}/api/expense/${id}`, {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
          },
        });

        if (response.status === 200) {
          await Swal.fire('Deleted!', 'Expense has been deleted.', 'success');
          window.location.reload();
        } else {
          Swal.fire('Error', response.data.message || 'Failed to delete expense', 'error');
        }
      } catch (error) {
        Swal.fire('Error', 'Failed to delete expense: ' + error.message, 'error');
      }
    }
  };

  const handleTruckDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You won’t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(API_URL+"/api/truck/delete-expense/"+id,{method : 'DELETE' },
        );
        const json = await response.json();
        if (response.status === 200) {
          await Swal.fire('Deleted!', 'Expense has been deleted.', 'success');
          window.location.reload();
        } else {
          Swal.fire('Error', response.data.message || 'Failed to delete expense', 'error');
        }
      } catch (error) {
        Swal.fire('Error', 'Failed to delete expense: ' + error.message, 'error');
      }
    }
  };

  const initializeDataTable = (data, tab) => {
    const tableRef = tab === 'office_operations' ? officeTableRef : tab === 'staff' ? staffTableRef : trucksTableRef;
    const dataTableRef =
      tab === 'office_operations'
        ? officeDataTableRef
        : tab === 'staff'
        ? staffDataTableRef
        : trucksDataTableRef;

    // Destroy existing DataTable if it exists
    if (dataTableRef.current) {
      dataTableRef.current.destroy();
      dataTableRef.current = null;
    }

    // Initialize DataTable only if table element exists
    if (tableRef.current) {
      dataTableRef.current = $(tableRef.current).DataTable({
        data: data,
        columns: [
          { data: 'id', title: 'ID' },
          { data: 'title', title: 'Title' },
          {
            data: 'amount',
            title: 'Amount',
            render: (data) => `$${parseFloat(data || 0).toFixed(2)}`,
          },
          {
            data: 'on_date',
            title: 'Date',
            render: (data) => (data && data !== '-' ? data.split('T')[0] : '-'),
          },
          {
            data: 'payment_method',
            title: 'Payment Method',
            render: (data) => (data && data !== '-' ? data.charAt(0).toUpperCase() + data.slice(1) : '-'),
          },
          {
            data: null,
            title: 'Actions',
            orderable: false,
            searchable: false,
            render: (data) =>
              tab === 'trucks'
                ? `
                  <button class="btn btn-success btn-sm view-btn" data-id="${data.id}">
                    <i class="bi bi-eye"></i> View
                  </button>
                  <button class="btn btn-primary btn-sm edit-btn" data-id="${data.id}">
                    <i class="bi bi-pencil"></i> Edit
                  </button>
                  <button class="btn btn-danger btn-sm delete-btn" data-id="${data.id}">
                    <i class="bi bi-trash"></i> Delete
                  </button>
                `
                : `
                  <button class="btn btn-success btn-sm view-btn" data-id="${data.id}">
                    <i class="bi bi-eye"></i> View
                  </button>
                  <button class="btn btn-primary btn-sm edit-btn" data-id="${data.id}">
                    <i class="bi bi-pencil"></i> Edit
                  </button>
                  <button class="btn btn-danger btn-sm delete-btn" data-id="${data.id}">
                    <i class="bi bi-trash"></i> Delete
                  </button>
                `,
          },
        ],
        responsive: true,
        order: [[0, 'desc']],
        pageLength: 10,
        dom: 'Bfrtip',
        buttons: ['copy', 'csv', 'excel', 'print'],
        language: {
          emptyTable: 'No data available in table',
        },
      });

      // Attach event listeners
      $(tableRef.current).off('click').on('click', '.view-btn', function () {
        const id = $(this).data('id');
        navigate(tab === 'trucks' ? `/truck-expense/view/${id}` : `/expense/view/${id}`);
      });
        $(tableRef.current).on('click', '.edit-btn', function () {
          const id = $(this).data('id');
          navigate(tab === 'trucks' ? `/truck-expense/edit/${id}` : `/expense/edit/${id}`);
    
        });

        $(tableRef.current).on('click', '.delete-btn', function () {
          const id = $(this).data('id');
          tab === 'trucks' ? handleTruckDelete(id)  : handleDelete(id)
        
        });
    }
  };

  // Initialize DataTable when activeTab or data changes
  useEffect(() => {
    if (expenses.length || trucksExpenses.length) {
      const data =
        activeTab === 'office_operations'
          ? expenses.filter((e) => e.expense_type === 'office_operations')
          : activeTab === 'staff'
          ? expenses.filter((e) => e.expense_type === 'staff')
          : trucksExpenses;
      initializeDataTable(data, activeTab);
    }
  }, [activeTab, expenses, trucksExpenses]);

  return (
    <MasterLayout>
    
      <div className="container py-5">
        {/* <ExpenseSummaryCard expenses={expenses} trucksExpenses={trucksExpenses} /> */}
        <div className="row justify-content-center">
          <div className="col-lg-12">
            <div className="card shadow-sm border-0">
              <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                <h6 className="h6 mb-0">Expenses</h6>
              </div>
              <div className="card-body">
                <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                  <Nav variant="tabs" className="mb-3">
                    <Nav.Item>
                      <Nav.Link eventKey="office_operations">
                        Office Operations (
                        {expenses.filter((e) => e.expense_type === 'office_operations').length})
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="staff">
                        Staff ({expenses.filter((e) => e.expense_type === 'staff').length})
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="trucks">
                        Trucks ({trucksExpenses.length})
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                  <Tab.Content>
                    <Tab.Pane eventKey="office_operations">
                      <div className="table-responsive">
                        <table
                          ref={officeTableRef}
                          className="table table-bordered table-hover"
                          style={{ width: '100%' }}
                        >
                          <thead className="table-light">
                            <tr>
                              <th>ID</th>
                              <th>Title</th>
                              <th>Amount</th>
                              <th>Date</th>
                              <th>Payment Method</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody></tbody>
                        </table>
                      </div>
                    </Tab.Pane>
                    <Tab.Pane eventKey="staff">
                      <div className="table-responsive">
                        <table
                          ref={staffTableRef}
                          className="table table-bordered table-hover"
                          style={{ width: '100%' }}
                        >
                          <thead className="table-light">
                            <tr>
                              <th>ID</th>
                              <th>Title</th>
                              <th>Amount</th>
                              <th>Date</th>
                              <th>Payment Method</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody></tbody>
                        </table>
                      </div>
                    </Tab.Pane>
                    <Tab.Pane eventKey="trucks">
                      <div className="table-responsive">
                        <table
                          ref={trucksTableRef}
                          className="table table-bordered table-hover"
                          style={{ width: '100%' }}
                        >
                          <thead className="table-light">
                            <tr>
                              <th>ID</th>
                              <th>Title</th>
                              <th>Amount</th>
                              <th>Date</th>
                              <th>Payment Method</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody></tbody>
                        </table>
                      </div>
                    </Tab.Pane>
                  </Tab.Content>
                </Tab.Container>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default ExpenseTablePage;